import { produce } from 'immer';
import { isObject } from 'lodash/fp';
import { extractError } from 'app/lib/error';
import { QueryState } from 'app/lib/constants';
import { fqnEquals } from 'app/lib/utils';
import { DELETE_ATTRIBUTE_VALUE, SET_ATTRIBUTE_VALUE } from 'app/store/attributes/types.js';
import { DELETE_SNOWFLAKE_TABLE } from 'app/store/snowflake/types.js';
import { UPDATE_ENTITY_DESCRIPTION_SUCCESS_ENTITY_DESCRIPTION_UPDATED } from 'app/store/__generated__/events';
import {
    GET_COLUMN_METADATA,
    GET_SNOWFLAKE_TABLE_COLUMNS,
    SELECT_TAB,
    SELECT_TABLE
} from './types.js';

export const INITIAL_STATE = {
    selectedFqn: null,
    staleFqn: null,
    selectedTab: 'preview',
    columns: {
        fqn: null,
        metricsState: QueryState.IDLE,
        state: QueryState.IDLE,
        schema: null,
        properties: null,
        finishedAt: null,
        error: null
    }
};

export const findDatasetsReducer = produce((draft, action) => {
    switch (action.type) {
        case SELECT_TABLE:
            draft.selectedFqn = action.payload.fqn;
            break;

        case SELECT_TAB:
            draft.selectedTab = action.payload.id;
            break;

        case DELETE_SNOWFLAKE_TABLE.PROGRESS: {
            const { database, schema, table } = action.params;
            const deletedFqn = { database, schema, table };

            if (fqnEquals(draft.selectedFqn, deletedFqn)) {
                draft.selectedFqn = null;
                draft.staleFqn = deletedFqn;
            }
            if (fqnEquals(draft.columns.fqn, deletedFqn)) {
                draft.columns = INITIAL_STATE.columns;
            }
            break;
        }

        case DELETE_SNOWFLAKE_TABLE.SUCCEEDED:
            draft.staleFqn = null;
            break;

        case GET_SNOWFLAKE_TABLE_COLUMNS.STARTED:
            draft.columns.fqn = action.meta.fqn;
            draft.columns.state = QueryState.RUNNING;
            draft.columns.schema = null;
            draft.columns.properties = null;
            draft.columns.finishedAt = null;
            break;
        case UPDATE_ENTITY_DESCRIPTION_SUCCESS_ENTITY_DESCRIPTION_UPDATED: {
            const { assetId, description } = action.payload;
            const { column } = assetId;

            if (draft.columns.properties?.hasOwnProperty(column)) {
                draft.columns.properties[column].description = description;
            }

            return;
        }
        case GET_COLUMN_METADATA.STARTED: {
            draft.columns.state = QueryState.RUNNING;
            draft.columns.metricsState = QueryState.RUNNING;
            break;
        }

        case GET_SNOWFLAKE_TABLE_COLUMNS.SUCCEEDED: {
            draft.columns.schema = action.payload.columns;
            draft.columns.state = QueryState.SUCCESS;
            draft.columns.finishedAt = new Date().toISOString();
            break;
        }

        case GET_SNOWFLAKE_TABLE_COLUMNS.FAILED:
        case GET_COLUMN_METADATA.FAILED: {
            draft.columns.error = extractError(action.payload);
            draft.columns.state = QueryState.FAILED;
            draft.columns.metricsState = QueryState.FAILED;
            break;
        }

        case GET_SNOWFLAKE_TABLE_COLUMNS.CANCELED: {
            draft.columns.state = QueryState.CANCELED;
            break;
        }

        case GET_COLUMN_METADATA.SUCCEEDED: {
            const { columnProperties, descriptionByColumnName } = action.payload;
            const { columnNames } = action.params;

            if (!fqnEquals(action.meta.fqn, draft.columns.fqn)) {
                return;
            }

            const properties = {};

            for (const column of columnNames) {
                properties[column] = {};
            }

            for (const name in descriptionByColumnName) {
                properties[name].description = descriptionByColumnName[name];
            }

            for (const p of columnProperties) {
                properties[p.columnName][p.definitionUid] = p.value;
            }

            draft.columns.state = QueryState.SUCCESS;
            draft.columns.metricsState = QueryState.SUCCESS;
            draft.columns.properties = properties;
            break;
        }

        case SET_ATTRIBUTE_VALUE.SUCCEEDED: {
            // all we care about here are column attributes
            // table level attributes are in the tableDetails slice
            const { entityId, definitionUid, payload } = action.payload;

            if (isObject(entityId)) {
                const { database, schema, table, column } = entityId;
                const tableFqn = { database, schema, table };

                if (!column) {
                    return;
                }

                if (fqnEquals(tableFqn, draft.columns.fqn)) {
                    draft.columns.properties[column][definitionUid] = payload.value;
                }
            }

            break;
        }
        case DELETE_ATTRIBUTE_VALUE.SUCCEEDED: {
            // all we care about here are column attributes
            // table level attributes are in the tableDetails slice
            const { entityId, definitionUid } = action.payload;

            if (isObject(entityId)) {
                const { database, schema, table, column } = entityId;
                const tableFqn = { database, schema, table };

                if (!column) {
                    return;
                }

                if (fqnEquals(tableFqn, draft.columns.fqn)) {
                    delete draft.columns.properties[column][definitionUid];
                }
            }

            break;
        }

        default:
            break;
    }
}, INITIAL_STATE);
